/**
 * EL «⋯» QUE ELIGE CON QUÉ MÁQUINA (F2) Y QUIÉN (F3) HACE UNA APLICACIÓN (plan
 * del cuaderno, 26-09-2026). Dos sitios lo usan:
 *
 *  - CREAR/EDITAR TAREA → «Maquinaria…» y «Aplicador…» (`useTaskApplicationChoice`).
 *    El aplicador sirve sobre todo para una EMPRESA DE SERVICIOS, que no es una
 *    cuenta y no se puede asignar; sin elegir, es el asignado (D7). Crear tarea
 *    lo monta MUCHA gente: el planificador, Tareas, la ficha del trabajador, el
 *    perito, la pantalla del trabajador y el chat del asistente (`TaskFormDialog`
 *    se importa desde 17 sitios). Por eso lleva GUARDA DE ROL: solo el admin y el
 *    encargado eligen la máquina de una tarea; el perito y el trabajador no ven
 *    el «⋯». Una tarea ya COMPLETADA tampoco: la máquina de un parte hecho se
 *    cambia desde el parte (F123), no desde la tarea.
 *  - EL REGISTRO MANUAL → «Cambiar maquinaria» (`SprayLogFormDialog`, solo al
 *    dar de alta).
 *
 * Elegir es opcional y lo normal es NO elegir: sin elección, el servidor pone
 * al completar la de por defecto (finca → empresa → la única;
 * `_shared/applicationContext.ts`).
 *
 * `taskColumns()` devuelve SOLO lo que cambió: una tarea que nadie toca no manda
 * la columna, así que editarla desde un bundle viejo o sin permiso nunca borra
 * una elección, y crear una tarea sin elegir escribe lo mismo que antes de F2.
 */
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { SheetMoreMenuItem } from "@/components/ui/SheetMoreMenu";
import { ApplicationChoiceSheet } from "@/components/applicationContext/ApplicationChoiceSheet";
import { equipmentChoiceOptions } from "@/components/machinery/machineryText";
import { fetchCompanyEquipment, type CompanyEquipment } from "@/lib/machinery/equipmentApi";
import { equipmentLabel } from "@/lib/machinery/equipmentRules";
import { applicatorChoiceOptions } from "@/components/applicators/applicatorText";
import { fetchCompanyApplicators, type CompanyApplicator } from "@/lib/applicators/applicatorApi";

/** Quién elige la máquina de una tarea (D6: la da de alta el admin; la elige quien planifica). */
export function canChooseTaskApplication(role: string | null | undefined): boolean {
  return role === "admin" || role === "manager";
}

type Sheet = "equipment" | "applicator" | null;

/** El núcleo: estado, lista, línea del «⋯» y la hoja para elegir. */
export function useApplicationChoice({
  open,
  companyId,
  enabled,
  initialEquipmentId,
  machineryMenuLabel,
  initialApplicatorId = null,
  applicatorMenuLabel,
  testIdPrefix,
}: {
  open: boolean;
  companyId: string | null | undefined;
  enabled: boolean;
  initialEquipmentId: string | null;
  /** «Maquinaria…» en crear tarea, «Cambiar maquinaria» en el registro manual. */
  machineryMenuLabel: string;
  initialApplicatorId?: string | null;
  /** «Aplicador…»; sin él, el «⋯» no ofrece elegir aplicador. */
  applicatorMenuLabel?: string;
  testIdPrefix: string;
}) {
  const { t } = useTranslation();
  const [equipmentId, setEquipmentId] = useState<string | null>(initialEquipmentId);
  const [equipment, setEquipment] = useState<CompanyEquipment[]>([]);
  const [applicatorId, setApplicatorId] = useState<string | null>(initialApplicatorId);
  const [applicators, setApplicators] = useState<CompanyApplicator[]>([]);
  const [sheet, setSheet] = useState<Sheet>(null);
  const withApplicator = !!applicatorMenuLabel;

  // Cada vez que se abre, se parte de lo que tiene la tarea (o de nada).
  useEffect(() => {
    if (open) {
      setEquipmentId(initialEquipmentId);
      setApplicatorId(initialApplicatorId);
    } else setSheet(null);
  }, [open, initialEquipmentId, initialApplicatorId]);

  useEffect(() => {
    if (!open || !enabled || !withApplicator || !companyId) return;
    let alive = true;
    fetchCompanyApplicators(companyId)
      .then((list) => {
        if (alive) setApplicators(list.filter((a) => a.active));
      })
      .catch((error) => console.error("useApplicationChoice: no se pudieron leer los carnés", error));
    return () => {
      alive = false;
    };
  }, [open, enabled, withApplicator, companyId]);

  // La lista solo se pide si el «⋯» se puede usar: a quien no lo ve no le cuesta nada.
  useEffect(() => {
    if (!open || !enabled || !companyId) return;
    let alive = true;
    fetchCompanyEquipment(companyId)
      .then((list) => {
        if (alive) setEquipment(list.filter((e) => e.active));
      })
      .catch((error) => console.error("useApplicationChoice: no se pudo leer la maquinaria", error));
    return () => {
      alive = false;
    };
  }, [open, enabled, companyId]);

  const chosenEquipment = equipment.find((e) => e.id === equipmentId) ?? null;
  const options = useMemo(() => equipmentChoiceOptions(equipment, t), [equipment, t]);
  const chosenApplicator = applicators.find((a) => a.id === applicatorId) ?? null;
  const applicatorOptions = useMemo(() => applicatorChoiceOptions(applicators, t), [applicators, t]);

  /* El menú se cierra y devuelve el foco a su botón ANTES de abrir la hoja: si
     se abren a la vez, el foco vuelve al «⋯» por detrás de la hoja nueva. */
  const openLater = (which: Exclude<Sheet, null>) => window.setTimeout(() => setSheet(which), 0);

  const menu = enabled ? (
    <>
      <SheetMoreMenuItem onSelect={() => openLater("equipment")} data-testid={`${testIdPrefix}-menu-machinery`}>
        <div className="min-w-0">
          <div>{machineryMenuLabel}</div>
          <div className="text-xs text-muted-foreground truncate">
            {chosenEquipment ? equipmentLabel(chosenEquipment) : t("applicationContext.auto")}
          </div>
        </div>
      </SheetMoreMenuItem>
      {withApplicator && (
        <SheetMoreMenuItem onSelect={() => openLater("applicator")} data-testid={`${testIdPrefix}-menu-applicator`}>
          <div className="min-w-0">
            <div>{applicatorMenuLabel}</div>
            <div className="text-xs text-muted-foreground truncate">
              {chosenApplicator ? chosenApplicator.display_name : t("applicationContext.applicators.autoShort")}
            </div>
          </div>
        </SheetMoreMenuItem>
      )}
    </>
  ) : undefined;

  const sheets = enabled ? (
    <ApplicationChoiceSheet
      open={sheet === "equipment"}
      onOpenChange={(v) => setSheet(v ? "equipment" : null)}
      title={t("applicationContext.machinery.chooseTitle")}
      autoLabel={t("applicationContext.auto")}
      autoDetail={t("applicationContext.machinery.autoDetail")}
      options={options}
      value={equipmentId}
      onChange={setEquipmentId}
      emptyText={t("applicationContext.machinery.noneToChoose")}
      testId={`${testIdPrefix}-machinery-choice`}
    />
  ) : null;

  const applicatorSheet = enabled && withApplicator ? (
    <ApplicationChoiceSheet
      open={sheet === "applicator"}
      onOpenChange={(v) => setSheet(v ? "applicator" : null)}
      title={t("applicationContext.applicators.chooseTitle")}
      autoLabel={t("applicationContext.applicators.autoShort")}
      autoDetail={t("applicationContext.applicators.autoDetail")}
      options={applicatorOptions}
      value={applicatorId}
      onChange={setApplicatorId}
      emptyText={t("applicationContext.applicators.noneToChoose")}
      testId={`${testIdPrefix}-applicator-choice`}
    />
  ) : null;

  return {
    enabled,
    equipmentId,
    chosenEquipment,
    applicatorId,
    /** Las listas ya leídas (activas): para los avisos de carné y máquina (F4). */
    equipment,
    applicators,
    menu,
    sheets: (
      <>
        {sheets}
        {applicatorSheet}
      </>
    ),
    initialEquipmentId,
    initialApplicatorId,
  };
}

interface TaskLike {
  status?: string | null;
  equipment_id?: string | null;
  applicator_id?: string | null;
}

/** Crear/editar tarea: guarda de rol y columnas de `planner_tasks` que cambiaron. */
export function useTaskApplicationChoice({
  open,
  companyId,
  role,
  task,
}: {
  open: boolean;
  companyId: string | null | undefined;
  role: string | null | undefined;
  task?: TaskLike | null;
}) {
  const { t } = useTranslation();
  const choice = useApplicationChoice({
    open,
    companyId,
    enabled: canChooseTaskApplication(role) && task?.status !== "completed",
    initialEquipmentId: task?.equipment_id ?? null,
    machineryMenuLabel: t("applicationContext.task.machineryMenu"),
    initialApplicatorId: task?.applicator_id ?? null,
    applicatorMenuLabel: t("applicationContext.task.applicatorMenu"),
    testIdPrefix: "task",
  });

  /** Las columnas de `planner_tasks` que cambiaron (ver la cabecera). */
  const columns = (): Record<string, string | null> => {
    const out: Record<string, string | null> = {};
    if (choice.enabled && choice.equipmentId !== choice.initialEquipmentId) out.equipment_id = choice.equipmentId;
    if (choice.enabled && choice.applicatorId !== choice.initialApplicatorId) out.applicator_id = choice.applicatorId;
    return out;
  };

  return {
    menu: choice.menu,
    sheets: choice.sheets,
    columns,
    /* F4 (26-09-2026): con qué máquina y quién, para avisar de un carné
       caducado o una inspección vencida al crear la tarea. Solo hay listas
       cuando el «⋯» se puede usar (admin y encargado). */
    enabled: choice.enabled,
    equipmentId: choice.equipmentId,
    applicatorId: choice.applicatorId,
    equipment: choice.equipment,
    applicators: choice.applicators,
  };
}
